import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ApiErrorCode,
  PUBLIC_PURPOSE_MAX_LENGTH,
  SELECTABLE_TRIP_TYPES,
  SLOT_MINUTES,
  SystemRole,
  TRIP_TYPE_LABELS,
  TripType,
  isOnSlotBoundary,
} from '@fleet/shared';
import { ApiError } from '../api/client';
import { bookingsApi } from '../api/bookings';
import { vehiclesApi } from '../api/vehicles';
import { usersApi } from '../api/users';
import { useAuth } from '../auth/useAuth';
import { PrivacyConsent } from '../privacy/PrivacyConsent';
import { usePrivacyNotice } from '../privacy/usePrivacyNotice';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { DateTimeInput, Field, inputClass } from '../ui/Field';
import { ErrorNotice } from '../ui/ErrorNotice';

interface Props {
  /** Vorbelegung aus dem Kalender. Fehlt sie, öffnet der Dialog leer. */
  initial?: { vehicleId?: string; from?: Date; to?: Date };
  onClose: () => void;
}

/**
 * Buchungsformular.
 *
 * Fahrzeug, Datum und Uhrzeit sind hier vollwertige Eingabefelder und nicht nur
 * eine Zusammenfassung dessen, was im Kalender gezogen wurde. Das hat zwei
 * Gründe: Aus der Wochenansicht lässt sich nicht sinnvoll ziehen, und wer sich
 * beim Ziehen um eine Viertelstunde vertan hat, korrigiert das hier, statt den
 * Dialog zu schließen und es noch einmal zu versuchen.
 */
export function BookingDialog({ initial, onClose }: Props) {
  const queryClient = useQueryClient();

  const auth = useAuth();

  const vehicles = useQuery({
    queryKey: ['vehicles', { includeInactive: false }],
    queryFn: () => vehiclesApi.list(false),
  });
  const bookable = (vehicles.data ?? []).filter((vehicle) => vehicle.canBook);

  /**
   * Für andere buchen dürfen Manager und die Fuhrparkleitung.
   *
   * `canApproveBookings` ist der grobe Hinweis, ob das überhaupt in Frage
   * kommt; ob es für die *gewählte* Fahrzeuggruppe gilt, entscheidet der
   * Server — dort hängt es an der Freigabeberechtigung dieser Gruppe.
   */
  const mayBookForOthers = auth.me?.canApproveBookings ?? false;
  const people = useQuery({
    queryKey: ['users'],
    queryFn: () => usersApi.list(),
    enabled: mayBookForOthers,
    staleTime: 5 * 60_000,
  });

  const defaults = useMemo(() => initialValues(initial), [initial]);

  const [vehicleId, setVehicleId] = useState(defaults.vehicleId);
  const [startDate, setStartDate] = useState(defaults.startDate);
  const [startTime, setStartTime] = useState(defaults.startTime);
  const [endDate, setEndDate] = useState(defaults.endDate);
  const [endTime, setEndTime] = useState(defaults.endTime);

  const [driverId, setDriverId] = useState('');
  const [tripType, setTripType] = useState<TripType>(TripType.DIENSTLICH);
  const [purpose, setPurpose] = useState('');
  /**
   * Kurztext für den Kalender.
   *
   * Er folgt dem Zweck, solange niemand ihn angefasst hat — danach nicht mehr.
   * Ohne dieses Merken würde eine spätere Korrektur am Zweck den bewusst
   * gewählten Kalendertext wieder überschreiben, und zwar unbemerkt: Das Feld
   * steht weiter oben und ist beim Tippen im Zweckfeld womöglich gar nicht im
   * Blick.
   */
  const [publicPurpose, setPublicPurpose] = useState('');
  const [publicPurposeTouched, setPublicPurposeTouched] = useState(false);
  const [destination, setDestination] = useState('');
  const [passengers, setPassengers] = useState('');
  const [notes, setNotes] = useState('');
  const [privacyAccepted, setPrivacyAccepted] = useState(false);

  const from = combine(startDate, startTime);
  const to = combine(endDate, endTime);

  const selected = bookable.find((vehicle) => vehicle.id === vehicleId);

  const privacy = usePrivacyNotice();

  /**
   * Wer muss die Datenschutzhinweise bestätigen?
   *
   * Alle außer Fuhrparkleitung, Administration und den Managern **dieser**
   * Fahrzeuggruppe. Deshalb hängt es am gewählten Fahrzeug und nicht an der
   * Person: Wer die Poolfahrzeuge freigibt, ist bei den Wagen der
   * Geschäftsführung ein gewöhnlicher Nutzer und bestätigt dort wie alle
   * anderen. Dieselbe Regel prüft der Server noch einmal — hier steht sie, damit
   * das Häkchen nicht dort erscheint, wo es sinnlos ist.
   *
   * Ohne gewähltes Fahrzeug wird die Bestätigung verlangt: Das ist die
   * vorsichtigere Annahme, und ohne Fahrzeug lässt sich ohnehin nicht buchen.
   */
  const isFleetManager = auth.hasRole(SystemRole.ADMIN) || auth.hasRole(SystemRole.FLEET_MANAGER);
  const needsPrivacyConsent = !isFleetManager && !(selected?.canApprove ?? false);

  /**
   * Prüfungen, die auch die Datenbank kennt.
   *
   * Das Raster steckt im Schema *und* in einem CHECK-Constraint. Hier steht es
   * ein drittes Mal, weil ein Hinweis am Feld mehr wert ist als ein 400 nach
   * dem Absenden — die Wahrheit bleibt aber die Datenbank.
   */
  const isPrivate = tripType === TripType.PRIVAT;

  const problems: string[] = [];
  if (!vehicleId) problems.push('Bitte ein Fahrzeug wählen.');
  if (!from || !to) problems.push('Bitte Datum und Uhrzeit vollständig angeben.');
  if (from && to && to <= from) problems.push('Das Ende muss nach dem Beginn liegen.');
  if ((from && !isOnSlotBoundary(from)) || (to && !isOnSlotBoundary(to))) {
    problems.push(`Zeiten müssen auf ${SLOT_MINUTES}-Minuten-Schritte fallen.`);
  }
  // Bei Privatfahrten ist das Feld gar nicht erst da — die Pflicht gilt nur für
  // die übrigen Fahrtarten.
  if (!isPrivate && !purpose.trim()) problems.push('Bitte den Zweck der Fahrt angeben.');
  if (needsPrivacyConsent && !(privacyAccepted && privacy.data)) {
    problems.push('Bitte die Datenschutzhinweise zur Kenntnis nehmen.');
  }

  const mutation = useMutation({
    mutationFn: () =>
      bookingsApi.create({
        vehicleId,
        // Weggelassen heißt: für sich selbst.
        driverId: driverId || undefined,
        startsAt: from!.toISOString(),
        endsAt: to!.toISOString(),
        intendedTripType: tripType,
        // Bei Privatfahrten werden Ziel und Zweck bewusst nicht übertragen —
        // steuerlich genügt die Kilometerangabe, alles Weitere wäre ein
        // vermeidbarer Eingriff in die Privatsphäre. Der Server weist sie
        // ohnehin zurück.
        purpose: tripType === TripType.PRIVAT ? undefined : purpose || undefined,
        publicPurpose: tripType === TripType.PRIVAT ? undefined : publicPurpose || undefined,
        destinationAddress: tripType === TripType.PRIVAT ? undefined : destination || undefined,
        passengers: passengers || undefined,
        notes: notes || undefined,
        /**
         * Die bestätigte Fassung, nicht ein Häkchen: Ändert sich der Text,
         * während das Formular offen steht, weist der Server die Buchung ab,
         * statt eine Kenntnisnahme des alten Wortlauts als die des neuen zu
         * verbuchen.
         */
        privacyNoticeVersion: privacyAccepted ? privacy.data?.version : undefined,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['availability'] });
      await queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      await queryClient.invalidateQueries({ queryKey: ['all-bookings'] });
      onClose();
    },
    /**
     * Hat sich der Hinweistext geändert, während das Formular offen stand, ist
     * die abgegebene Bestätigung wertlos — sie belegte einen anderen Wortlaut.
     * Der Text wird nachgeladen und das Häkchen zurückgenommen, damit erneut
     * bestätigt wird, was jetzt gilt, statt dieselbe Ablehnung ein zweites Mal
     * zu erzeugen.
     */
    onError: async (error: unknown) => {
      if (
        error instanceof ApiError &&
        error.code === ApiErrorCode.PRIVACY_NOTICE_NOT_ACKNOWLEDGED
      ) {
        setPrivacyAccepted(false);
        await queryClient.invalidateQueries({ queryKey: ['privacy-notice'] });
      }
    },
  });

  return (
    <Modal
      title={selected ? `${selected.licensePlate} buchen` : 'Fahrzeug buchen'}
      description={
        selected && !selected.privateUseAllowed
          ? 'Für dieses Fahrzeug sind keine Privatfahrten zugelassen.'
          : undefined
      }
      onClose={onClose}
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (problems.length === 0) mutation.mutate();
        }}
      >
        <Field label="Fahrzeug">
          <select
            value={vehicleId}
            onChange={(event) => setVehicleId(event.target.value)}
            className={inputClass}
          >
            <option value="">— bitte wählen —</option>
            {bookable.map((vehicle) => (
              <option key={vehicle.id} value={vehicle.id}>
                {vehicle.licensePlate} · {vehicle.displayName}
              </option>
            ))}
          </select>
        </Field>

        {vehicles.data && bookable.length === 0 && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            Für keine Fahrzeuggruppe liegt eine Buchungsberechtigung vor. Die Fuhrparkleitung
            vergibt sie unter Verwaltung → Gruppen → Berechtigungen.
          </p>
        )}

        {mayBookForOthers && (
          <Field
            label="Fahrer"
            hint="Leer lassen, um für dich selbst zu buchen. Auswählbar sind Personen, die sich bereits einmal angemeldet haben."
          >
            <select
              value={driverId}
              onChange={(event) => setDriverId(event.target.value)}
              className={inputClass}
            >
              <option value="">Für mich selbst</option>
              {people.data?.map((person) => (
                <option key={person.id} value={person.id}>
                  {person.displayName}
                  {person.email ? ` · ${person.email}` : ''}
                </option>
              ))}
            </select>
          </Field>
        )}

        {/* Untereinander — nebeneinander bliebe für das Datumsfeld zu wenig Platz. */}
        <div className="space-y-3">
          <Field label="Beginn">
            <DateTimeInput
              label="Beginn"
              date={startDate}
              time={startTime}
              onDateChange={(value) => {
                setStartDate(value);
                // Endtag mitziehen, solange er nicht bewusst abweicht — eine
                // Buchung über Nacht ist die Ausnahme, nicht der Fall, den man
                // beim Datumswechsel gemeint hat.
                if (endDate === startDate) setEndDate(value);
              }}
              onTimeChange={setStartTime}
              stepSeconds={SLOT_MINUTES * 60}
            />
          </Field>

          <Field label="Ende">
            <DateTimeInput
              label="Ende"
              date={endDate}
              time={endTime}
              onDateChange={setEndDate}
              onTimeChange={setEndTime}
              stepSeconds={SLOT_MINUTES * 60}
            />
          </Field>
        </div>

        <fieldset>
          <legend className="text-sm font-medium">Art der Fahrt</legend>
          <div className="mt-2 flex gap-2">
            {SELECTABLE_TRIP_TYPES.map((type) => (
              <label
                key={type}
                className={[
                  'flex-1 cursor-pointer rounded-md border px-3 py-2 text-center text-sm',
                  tripType === type
                    ? 'border-slate-900 bg-slate-900 text-white'
                    : 'border-slate-300 hover:bg-slate-50',
                ].join(' ')}
              >
                <input
                  type="radio"
                  name="tripType"
                  value={type}
                  checked={tripType === type}
                  onChange={() => setTripType(type)}
                  className="sr-only"
                />
                {TRIP_TYPE_LABELS[type]}
              </label>
            ))}
          </div>
        </fieldset>

        {isPrivate ? (
          <p className="rounded-md bg-slate-50 px-3 py-2.5 text-sm leading-relaxed text-slate-600">
            Bei Privatfahrten werden weder Ziel noch Zweck erfasst — steuerlich genügt die
            Kilometerangabe. Auch die Positionsdaten des Fahrzeugs werden für diese Fahrt
            anonymisiert.
          </p>
        ) : (
          <>
            <Field
              label="Zweck der Fahrt"
              hint="Konkret, z. B. „Angebotsübergabe Neubau Ost“ — nicht „Kundenbesuch“."
            >
              <input
                type="text"
                value={purpose}
                onChange={(event) => {
                  setPurpose(event.target.value);
                  // Nur mitziehen, solange der Kalendertext nicht selbst
                  // gesetzt wurde — sonst überschriebe eine Korrektur hier eine
                  // bewusste Entscheidung dort.
                  if (!publicPurposeTouched) {
                    setPublicPurpose(event.target.value.slice(0, PUBLIC_PURPOSE_MAX_LENGTH));
                  }
                }}
                maxLength={500}
                className={inputClass}
                placeholder="Angebotsübergabe Neubau Ost"
              />
            </Field>

            {/*
              Das Feld steht unmittelbar unter dem Zweck und ist vorbelegt, statt
              hinter einem „Details"-Aufklapper zu liegen. Wer nicht möchte, dass
              „Betriebsarzt" im geteilten Kalender steht, muss das sehen, bevor
              es dort steht — und nicht erst, wenn jemand nachfragt.
            */}
            <Field
              label="Im Kalender sichtbar"
              optional
              hint="Diese Zeile sehen alle im Kalender. Sie ist mit dem Zweck vorbelegt — trag etwas Allgemeineres ein, wenn der Anlass niemanden angeht."
            >
              <input
                type="text"
                value={publicPurpose}
                onChange={(event) => {
                  setPublicPurpose(event.target.value);
                  setPublicPurposeTouched(true);
                }}
                maxLength={PUBLIC_PURPOSE_MAX_LENGTH}
                className={inputClass}
                placeholder="Auswärtstermin"
              />
            </Field>

            <Field label="Ziel" optional hint="Straße, Hausnummer, PLZ und Ort.">
              <input
                type="text"
                value={destination}
                onChange={(event) => setDestination(event.target.value)}
                maxLength={500}
                className={inputClass}
                placeholder="Industriestraße 12, 20095 Hamburg"
              />
            </Field>
          </>
        )}

        <Field label="Mitfahrende" optional>
          <input
            type="text"
            value={passengers}
            onChange={(event) => setPassengers(event.target.value)}
            maxLength={500}
            className={inputClass}
          />
        </Field>

        <Field label="Notiz" optional>
          <textarea
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
            rows={2}
            maxLength={2000}
            className={inputClass}
          />
        </Field>

        {/*
          Unmittelbar über dem Buchen-Knopf und nicht am Anfang des Formulars:
          Ein Hinweis, der ganz oben steht, ist beim Absenden zwanzig Zeilen
          weit weggescrollt. Hier steht er dort, wo die Entscheidung fällt.
        */}
        <PrivacyConsent
          notice={privacy.data}
          error={privacy.error}
          required={needsPrivacyConsent}
          accepted={privacyAccepted}
          onChange={setPrivacyAccepted}
        />

        {problems.length > 0 && (
          <ul className="list-inside list-disc rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}

        {mutation.isError && <ErrorNotice error={mutation.error} />}

        <div className="flex justify-end gap-2 border-t border-slate-200 pt-4">
          <Button onClick={onClose}>Abbrechen</Button>
          <Button
            type="submit"
            variant="primary"
            disabled={problems.length > 0 || mutation.isPending}
          >
            {mutation.isPending ? 'Wird gebucht …' : 'Buchen'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/** Vorbelegung: aus dem Kalender übernommen, sonst die nächste freie Viertelstunde. */
function initialValues(initial: Props['initial']) {
  const from = initial?.from ?? nextSlot(new Date());
  const to = initial?.to ?? new Date(from.getTime() + 60 * 60_000);

  return {
    vehicleId: initial?.vehicleId ?? '',
    startDate: dateValue(from),
    startTime: timeValue(from),
    endDate: dateValue(to),
    endTime: timeValue(to),
  };
}

function nextSlot(instant: Date): Date {
  const result = new Date(instant);
  result.setSeconds(0, 0);
  result.setMinutes(Math.ceil(result.getMinutes() / SLOT_MINUTES) * SLOT_MINUTES);
  return result;
}

/**
 * `<input type="date">` und `type="time"` arbeiten in Ortszeit.
 *
 * Deshalb bewusst nicht über `toISOString()` — das rechnet nach UTC um und
 * verschiebt an Sommerabenden das Datum auf den Folgetag.
 */
function dateValue(instant: Date): string {
  return `${instant.getFullYear()}-${pad(instant.getMonth() + 1)}-${pad(instant.getDate())}`;
}

function timeValue(instant: Date): string {
  return `${pad(instant.getHours())}:${pad(instant.getMinutes())}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function combine(date: string, time: string): Date | undefined {
  if (!date || !time) return undefined;
  const [year, month, day] = date.split('-').map(Number);
  const [hour, minute] = time.split(':').map(Number);
  if ([year, month, day, hour, minute].some((part) => part === undefined || Number.isNaN(part))) {
    return undefined;
  }
  return new Date(year!, month! - 1, day!, hour!, minute!, 0, 0);
}
